import React from "react";
import * as THREE from "three";
import { Vector3 } from "three";
import { Line } from "@react-three/drei";
import { useThree } from "@react-three/fiber";
import { BeamTruss } from "../../types/beamTruss";
import { getLoadVisualScale } from "@/utils/gridScale";

// Global coordinate system axes (never change)
const GLOBAL_X = new Vector3(1, 0, 0);
const GLOBAL_Y = new Vector3(0, 1, 0);
const GLOBAL_Z = new Vector3(0, 0, 1);

interface LoadVisualization3DProps {
  beamTruss: BeamTruss;
  startPoint: Vector3;
  endPoint: Vector3;
  visible?: boolean;
  scaleFactor?: number;
}

// Zoom-adaptive arrow configuration (all colors black)
export const arrowConfig = {
  // Base (normalized) sizes in world units at zoom scale = 1
  base: {
    headLength: 0.25,
    headWidth: 0.3,
    tailLength: 1.0,
    tailThickness: 0.01,
    headSharpness: 0.6, // for V head shape
    lineWidth: 0.7, // for V lines
    triDepth: 0, // triangle plane (0 means no prism depth)
  },

  // Global scaling factor (multiplied with zoom scale)
  scaleFactor: 1,

  // Beam surface offsets (half-thickness) and epsilon for Y/Z directions
  beam: {
    halfThicknessY: 0, // set from beam section if available
    halfThicknessZ: 0, // set from beam section if available
    epsilon: 0.001,
  },

  // Zoom behavior constants
  zoom: {
    referenceDistance: 10, // perspective ref distance where zoom scale ~1
    orthoReferenceZoom: 100, // orthographic ref zoom where scale ~1
    minScale: 0.5,
    maxScale: 4,
  },

  // Adaptive arrow-count settings: try to keep arrows roughly this many pixels apart
  adaptive: {
    desiredScreenSpacingPx: 40, // preferred pixels between adjacent arrows
    minArrows: 2,
    maxArrows: 200,
  },

  dashed: {
    dashSize: 0.5,
    gapSize: 0.5,
  },

  colors: {
    x: "#FF0000",
    y: "#FF0000",
    z: "#FF0000",
  },
};

// Helper: compute equally spaced positions including endpoints
function computePositions(start: Vector3, end: Vector3, n: number): Vector3[] {
  if (n <= 1) return [start.clone()];
  const positions: Vector3[] = [];
  for (let i = 0; i < n; i++) {
    const t = i / (n - 1);
    positions.push(start.clone().lerp(end, t));
  }
  return positions;
}

// Helper: compute local beam axes (x along beam; y,z forming a right-handed frame)
function computeLocalAxes(start: Vector3, end: Vector3) {
  const x = end.clone().sub(start).normalize();
  // Choose a temp up that is not parallel to x
  const worldUp = new THREE.Vector3(0, 1, 0);
  const alt =
    Math.abs(x.dot(worldUp)) > 0.95 ? new THREE.Vector3(0, 0, 1) : worldUp;
  const z = new THREE.Vector3().crossVectors(x, alt).normalize();
  const y = new THREE.Vector3().crossVectors(z, x).normalize();
  return { x, y, z };
}

// Helper: check if a load direction is aligned with the beam axis
function isLoadAlignedWithBeam(
  loadDir: THREE.Vector3,
  beamAxisX: THREE.Vector3
): boolean {
  const dotProduct = Math.abs(loadDir.dot(beamAxisX));
  return dotProduct > 0.95;
}

// Helper: compute zoom-adaptive scale so on-screen size stays readable
function getZoomScale(camera: THREE.Camera, worldPos: Vector3): number {
  const cfg = arrowConfig.zoom;
  let scale = 1;
  // @ts-ignore
  if ((camera as any).isPerspectiveCamera) {
    const cam = camera as THREE.PerspectiveCamera;
    const distance = cam.position.clone().sub(worldPos).length();
    scale = distance / cfg.referenceDistance;
  } else {
    // Orthographic: inverse-proportional to zoom
    // @ts-ignore
    const cam = camera as any;
    const zoom = cam.zoom ?? cfg.orthoReferenceZoom;
    scale = (cfg.orthoReferenceZoom || 1) / zoom;
  }
  return (
    Math.min(cfg.maxScale, Math.max(cfg.minScale, scale)) *
    (arrowConfig.scaleFactor || 1)
  );
}

// Helper: get camera view direction (world)
function getCameraViewDir(camera: THREE.Camera): THREE.Vector3 {
  const dir = new THREE.Vector3();
  // camera.getWorldDirection returns -Z in world space by default
  camera.getWorldDirection(dir);
  return dir.normalize();
}

// Helper: choose a perpendicular in the beam's local YZ-plane that is most visible
function choosePerpForView(
  dir: THREE.Vector3,
  localY: THREE.Vector3,
  localZ: THREE.Vector3,
  camera?: THREE.Camera
) {
  // Build candidate list that is orthogonal to dir
  const candidates: THREE.Vector3[] = [];
  if (Math.abs(localY.dot(dir)) < 0.99) candidates.push(localY.clone());
  if (Math.abs(localZ.dot(dir)) < 0.99) candidates.push(localZ.clone());
  if (candidates.length === 0) {
    // Fallback: construct any orthogonal vector
    const any =
      Math.abs(dir.y) < 0.99
        ? new THREE.Vector3(0, 1, 0)
        : new THREE.Vector3(1, 0, 0);
    const ortho = new THREE.Vector3().crossVectors(dir, any).normalize();
    candidates.push(ortho);
  }

  // If camera provided, choose the most visible (most orthogonal to view direction)
  if (camera) {
    const viewDir = getCameraViewDir(camera);
    candidates.sort(
      (a, b) => Math.abs(a.dot(viewDir)) - Math.abs(b.dot(viewDir))
    );
  }

  return candidates[0].normalize();
}

// Draw V-style arrow head (X-direction), no tail
function drawVArrowHead(
  position: Vector3,
  axis: Vector3,
  color = arrowConfig.colors.x,
  sizeScale = 1,
  camera?: THREE.Camera,
  localY?: THREE.Vector3,
  localZ?: THREE.Vector3
) {
  const dir = axis.clone().normalize();
  const headLen = arrowConfig.base.headLength * sizeScale;
  const headWidth = arrowConfig.base.headWidth * sizeScale;
  const sharp = arrowConfig.base.headSharpness;
  const lineW = arrowConfig.base.lineWidth * sizeScale;

  const back = dir.clone().multiplyScalar(-headLen);
  // Build a perpendicular vector in the beam's local YZ plane for consistent local orientation
  let perp: THREE.Vector3;
  if (localY && localZ) {
    perp = choosePerpForView(dir, localY, localZ, camera);
  } else {
    const any =
      Math.abs(dir.y) < 0.99
        ? new THREE.Vector3(0, 1, 0)
        : new THREE.Vector3(1, 0, 0);
    perp = new THREE.Vector3().crossVectors(dir, any);
  }
  perp.normalize().multiplyScalar(headWidth * 0.5);
  const leftPoint = position
    .clone()
    .add(back)
    .add(perp.clone().multiplyScalar(sharp));
  const rightPoint = position
    .clone()
    .add(back)
    .add(perp.clone().multiplyScalar(-sharp));
  return (
    <group>
      <Line
        points={[
          [position.x, position.y, position.z],
          [leftPoint.x, leftPoint.y, leftPoint.z],
        ]}
        color={color}
        lineWidth={lineW}
      />
      <Line
        points={[
          [position.x, position.y, position.z],
          [rightPoint.x, rightPoint.y, rightPoint.z],
        ]}
        color={color}
        lineWidth={lineW}
      />
    </group>
  );
}

// Helper: create dashed tail segments for triangular arrows
function createDashedTailSegments(
  tailStart: Vector3,
  tailEnd: Vector3,
  color: string,
  tailThk: number,
  dir: Vector3
) {
  const dashConfig = arrowConfig.dashed;
  const totalLength = tailStart.distanceTo(tailEnd);
  const segmentLength = dashConfig.dashSize + dashConfig.gapSize;
  const numSegments = Math.ceil(totalLength / segmentLength);

  const segments: React.ReactNode[] = [];
  const tailQuat = new THREE.Quaternion().setFromUnitVectors(
    new THREE.Vector3(0, 1, 0),
    dir
  );

  for (let i = 0; i < numSegments; i++) {
    const segmentStart = i * segmentLength;
    const segmentEnd = Math.min(
      segmentStart + dashConfig.dashSize,
      totalLength
    );

    if (segmentEnd <= segmentStart) break;

    const t1 = segmentStart / totalLength;
    const t2 = segmentEnd / totalLength;
    const start = tailStart.clone().lerp(tailEnd, t1);
    const end = tailStart.clone().lerp(tailEnd, t2);
    const segLen = start.distanceTo(end);
    const segMid = start.clone().lerp(end, 0.5);

    segments.push(
      <mesh key={`dash-${i}`} position={segMid} quaternion={tailQuat}>
        <cylinderGeometry args={[tailThk, tailThk, segLen, 8]} />
        <meshBasicMaterial color={color} />
      </mesh>
    );
  }

  return segments;
}

// Draw triangular arrow with dashed tail (tip at beam surface) along a given axis
// Cone-shaped head with dashed cylindrical tail (Y/Z directions)
function drawTriangularArrow(
  position: Vector3,
  axis: THREE.Vector3,
  color: string,
  sizeScale = 1,
  _camera?: THREE.Camera,
  _localY?: THREE.Vector3,
  _localZ?: THREE.Vector3,
  tailScale = 1
) {
  const dir = axis.clone().normalize();
  const headLen = arrowConfig.base.headLength * sizeScale;
  const headWidth = arrowConfig.base.headWidth * sizeScale;
  const tailLen =
    arrowConfig.base.tailLength * sizeScale * Math.max(0, tailScale);
  const tailThk = arrowConfig.base.tailThickness * sizeScale;

  // Determine surface offset by direction (X, Y, or Z)
  const alongX = Math.abs(dir.x) > 0.5;
  const alongY = Math.abs(dir.y) > 0.5;
  const alongZ = Math.abs(dir.z) > 0.5;

  let surfaceOffset = arrowConfig.beam.epsilon;
  if (alongX) {
    surfaceOffset += arrowConfig.beam.halfThicknessY;
  } else if (alongY) {
    surfaceOffset += arrowConfig.beam.halfThicknessY;
  } else if (alongZ) {
    surfaceOffset += arrowConfig.beam.halfThicknessZ;
  }

  // Tip must touch beam surface; tip at position + dir * surfaceOffset
  const tip = position.clone().add(dir.clone().multiplyScalar(surfaceOffset));

  // Head base center is at tip + headLen along dir
  const baseCenter = tip.clone().add(dir.clone().multiplyScalar(headLen));

  // Tail from head base center outward
  const tailStart = baseCenter.clone();
  const tailEnd = baseCenter.clone().add(dir.clone().multiplyScalar(tailLen));

  // Cone head: align +Y to -dir so base points outward; center at tip + dir*(headLen/2) so apex sits at tip
  const coneQuat = new THREE.Quaternion().setFromUnitVectors(
    new THREE.Vector3(0, 1, 0),
    dir.clone().multiplyScalar(-1)
  );
  const coneCenter = tip.clone().add(dir.clone().multiplyScalar(headLen / 2));

  // Create dashed tail segments
  const dashedTailSegments = createDashedTailSegments(
    tailStart,
    tailEnd,
    color,
    tailThk,
    dir
  );

  return (
    <group>
      {/* Dashed tail segments */}
      {dashedTailSegments}

      {/* Conical head */}
      <mesh position={coneCenter} quaternion={coneQuat}>
        <coneGeometry args={[headWidth / 2, headLen, 16]} />
        <meshBasicMaterial color={color} />
      </mesh>
      {/* Conical head outline */}
      <mesh position={coneCenter} quaternion={coneQuat}>
        <coneGeometry args={[headWidth / 2 + 0.01, headLen + 0.01, 12]} />
        <meshBasicMaterial color="black" wireframe transparent opacity={0.6} />
      </mesh>
    </group>
  );
}

// Draw dashed load bar between first and last tail ends, offset along axis
// Removed dashed load bar per new requirements

// Helper: compute the tail end point for a Y/Z triangular arrow at a given position
function computeTriTailEnd(
  position: Vector3,
  axis: THREE.Vector3,
  sizeScale = 1,
  tailScale = 1
): Vector3 {
  const dir = axis.clone().normalize();
  const headLen = arrowConfig.base.headLength * sizeScale;
  const tailLen =
    arrowConfig.base.tailLength * sizeScale * Math.max(0, tailScale);

  const alongX = Math.abs(dir.x) > 0.5;
  const alongY = Math.abs(dir.y) > 0.5;
  const alongZ = Math.abs(dir.z) > 0.5;

  let surfaceOffset = arrowConfig.beam.epsilon;
  if (alongX) {
    surfaceOffset += arrowConfig.beam.halfThicknessY;
  } else if (alongY) {
    surfaceOffset += arrowConfig.beam.halfThicknessY;
  } else if (alongZ) {
    surfaceOffset += arrowConfig.beam.halfThicknessZ;
  }

  const tip = position.clone().add(dir.clone().multiplyScalar(surfaceOffset));
  const baseCenter = tip.clone().add(dir.clone().multiplyScalar(headLen));
  const tailEnd = baseCenter.clone().add(dir.clone().multiplyScalar(tailLen));
  return tailEnd;
}

export const LoadVisualization3D: React.FC<LoadVisualization3DProps> = ({
  beamTruss,
  startPoint,
  endPoint,
  visible = true,
  scaleFactor = 1,
}) => {
  const { camera, size } = useThree();
  if (!visible) return null;

  // Get load visual scale from pre-calculated fixed values
  // scaleFactor now represents scaleRange from useUIStore
  const visualScale = getLoadVisualScale(scaleFactor);

  const beamLength = startPoint.distanceTo(endPoint);

  // Adaptive arrow-count: aim for ~desiredScreenSpacingPx between arrows on screen
  const adaptiveCfg = arrowConfig.adaptive;
  const beamMid = startPoint.clone().lerp(endPoint, 0.5);

  const computePxPerUnit = () => {
    // @ts-ignore
    if ((camera as any).isPerspectiveCamera) {
      const cam = camera as THREE.PerspectiveCamera;
      const distance = cam.position.clone().sub(beamMid).length();
      const fovRad = (cam.fov * Math.PI) / 180;
      const worldUnitsPerScreenHeight = 2 * Math.tan(fovRad / 2) * distance;
      return worldUnitsPerScreenHeight > 0
        ? size.height / worldUnitsPerScreenHeight
        : 0;
    } else {
      const cam = camera as THREE.OrthographicCamera;
      const worldUnitsPerScreenHeight = (cam.top - cam.bottom) / cam.zoom;
      return worldUnitsPerScreenHeight > 0
        ? size.height / worldUnitsPerScreenHeight
        : 0;
    }
  };

  const pxPerUnit = computePxPerUnit();
  let autoCount = 0;
  if (pxPerUnit && pxPerUnit > 0 && isFinite(pxPerUnit)) {
    const unitsPerArrow = Math.max(
      1e-4,
      adaptiveCfg.desiredScreenSpacingPx / pxPerUnit
    );
    autoCount = Math.floor(beamLength / unitsPerArrow);
    autoCount = Math.max(
      adaptiveCfg.minArrows,
      Math.min(adaptiveCfg.maxArrows, autoCount)
    );
  } else {
    // Fallback heuristic, but keep adaptive min/max
    autoCount = Math.max(
      adaptiveCfg.minArrows,
      Math.min(adaptiveCfg.maxArrows, Math.floor(beamLength * 8))
    );
  }

  const count = Math.max(
    adaptiveCfg.minArrows,
    beamTruss.arrowCount || autoCount
  );
  const positions = computePositions(startPoint, endPoint, count);
  // Compute a representative zoom scale using beam midpoint, combined with model scale
  const zoomScale = getZoomScale(camera, beamMid) * visualScale;
  const {
    x: localX,
    y: localY,
    z: localZ,
  } = computeLocalAxes(startPoint, endPoint);

  const renderDistributedUniform = () => {
    const { dx = 0, dy = 0, dz = 0 } = beamTruss;
    const elements: React.ReactNode[] = [];
    const col = "#ff8533";

    // X-axis
    if (dx !== 0) {
      const useLineArrow = isLoadAlignedWithBeam(GLOBAL_X, localX);
      const axisX = GLOBAL_X.clone().multiplyScalar(
        useLineArrow ? (dx > 0 ? 1 : -1) : dx > 0 ? -1 : 1
      );

      if (useLineArrow) {
        positions.forEach((p, i) => {
          elements.push(
            <group key={`dx-${i}`}>
              {drawVArrowHead(p, axisX, col, zoomScale, camera, localY, localZ)}
            </group>
          );
        });
      } else {
        const tailPoints: [number, number, number][] = [];
        positions.forEach((p, i) => {
          elements.push(
            <group key={`dx-${i}`}>
              {drawTriangularArrow(
                p,
                axisX,
                col,
                zoomScale,
                camera,
                localY,
                localZ
              )}
            </group>
          );
          const tail = computeTriTailEnd(p, axisX, zoomScale, 1);
          tailPoints.push([tail.x, tail.y, tail.z]);
        });
        if (tailPoints.length >= 2) {
          elements.push(
            <Line
              key="dx-dashed"
              points={tailPoints}
              color={col}
              lineWidth={1}
              dashed
              dashSize={arrowConfig.dashed.dashSize}
              gapSize={arrowConfig.dashed.gapSize}
            />
          );
        }
      }
    }

    // Y-axis
    if (dy !== 0) {
      const useLineArrow = isLoadAlignedWithBeam(GLOBAL_Y, localX);
      const axisY = GLOBAL_Y.clone().multiplyScalar(
        useLineArrow ? (dy > 0 ? 1 : -1) : dy > 0 ? -1 : 1
      );

      if (useLineArrow) {
        positions.forEach((p, i) => {
          elements.push(
            <group key={`dy-${i}`}>
              {drawVArrowHead(p, axisY, col, zoomScale, camera, localY, localZ)}
            </group>
          );
        });
      } else {
        const tailPoints: [number, number, number][] = [];
        positions.forEach((p, i) => {
          elements.push(
            <group key={`dy-${i}`}>
              {drawTriangularArrow(
                p,
                axisY,
                col,
                zoomScale,
                camera,
                localY,
                localZ
              )}
            </group>
          );
          const tail = computeTriTailEnd(p, axisY, zoomScale, 1);
          tailPoints.push([tail.x, tail.y, tail.z]);
        });
        if (tailPoints.length >= 2) {
          elements.push(
            <Line
              key="dy-dashed"
              points={tailPoints}
              color={col}
              lineWidth={1}
              dashed
              dashSize={arrowConfig.dashed.dashSize}
              gapSize={arrowConfig.dashed.gapSize}
            />
          );
        }
      }
    }

    // Z-axis
    if (dz !== 0) {
      const useLineArrow = isLoadAlignedWithBeam(GLOBAL_Z, localX);
      const axisZ = GLOBAL_Z.clone().multiplyScalar(
        useLineArrow ? (dz > 0 ? 1 : -1) : dz > 0 ? -1 : 1
      );

      if (useLineArrow) {
        positions.forEach((p, i) => {
          elements.push(
            <group key={`dz-${i}`}>
              {drawVArrowHead(p, axisZ, col, zoomScale, camera, localY, localZ)}
            </group>
          );
        });
      } else {
        const tailPoints: [number, number, number][] = [];
        positions.forEach((p, i) => {
          elements.push(
            <group key={`dz-${i}`}>
              {drawTriangularArrow(
                p,
                axisZ,
                col,
                zoomScale,
                camera,
                localY,
                localZ
              )}
            </group>
          );
          const tail = computeTriTailEnd(p, axisZ, zoomScale, 1);
          tailPoints.push([tail.x, tail.y, tail.z]);
        });
        if (tailPoints.length >= 2) {
          elements.push(
            <Line
              key="dz-dashed"
              points={tailPoints}
              color={col}
              lineWidth={1}
              dashed
              dashSize={arrowConfig.dashed.dashSize}
              gapSize={arrowConfig.dashed.gapSize}
            />
          );
        }
      }
    }

    return <group>{elements}</group>;
  };

  const renderDistributedVariable = () => {
    const {
      dxLeft = 0,
      dyLeft = 0,
      dzLeft = 0,
      dxRight = 0,
      dyRight = 0,
      dzRight = 0,
    } = beamTruss;
    const elements: React.ReactNode[] = [];
    const col = "#a79dff";

    const maxAbsY = Math.max(Math.abs(dyLeft), Math.abs(dyRight));
    const maxAbsZ = Math.max(Math.abs(dzLeft), Math.abs(dzRight));
    const normY = maxAbsY > 0 ? maxAbsY : 1;
    const normZ = maxAbsZ > 0 ? maxAbsZ : 1;

    // Check X direction
    if (dxLeft !== 0 || dxRight !== 0) {
      const sampleAxisX = GLOBAL_X.clone();
      const useLineArrow = isLoadAlignedWithBeam(sampleAxisX, localX);

      if (useLineArrow) {
        // Line arrows for aligned loads
        positions.forEach((p, i) => {
          const t = i / (positions.length - 1);
          const mag = dxLeft + (dxRight - dxLeft) * t;
          if (mag !== 0) {
            const axisX = GLOBAL_X.clone().multiplyScalar(
              useLineArrow ? (mag > 0 ? 1 : -1) : mag > 0 ? -1 : 1
            );
            elements.push(
              <group key={`vdx-${i}`}>
                {drawVArrowHead(
                  p,
                  axisX,
                  col,
                  zoomScale,
                  camera,
                  localY,
                  localZ
                )}
              </group>
            );
          }
        });
      } else {
        // Diamond arrows for perpendicular loads
        const tailPoints: [number, number, number][] = [];
        const maxAbsX = Math.max(Math.abs(dxLeft), Math.abs(dxRight));
        const normX = maxAbsX > 0 ? maxAbsX : 1;

        positions.forEach((p, i) => {
          const t = i / (positions.length - 1);
          const mag = dxLeft + (dxRight - dxLeft) * t;
          if (mag !== 0) {
            const axisX = GLOBAL_X.clone().multiplyScalar(
              useLineArrow ? (mag > 0 ? 1 : -1) : mag > 0 ? -1 : 1
            );
            const rawScale = Math.abs(mag) / normX;
            const tailScale = Math.max(0.1, Math.min(1, rawScale));
            elements.push(
              <group key={`vdx-${i}`}>
                {drawTriangularArrow(
                  p,
                  axisX,
                  col,
                  zoomScale,
                  camera,
                  localY,
                  localZ,
                  tailScale
                )}
              </group>
            );
            const tail = computeTriTailEnd(p, axisX, zoomScale, tailScale);
            tailPoints.push([tail.x, tail.y, tail.z]);
          }
        });
        if (tailPoints.length >= 2) {
          elements.push(
            <Line
              key="vdx-dashed"
              points={tailPoints}
              color={col}
              lineWidth={1}
              dashed
              dashSize={arrowConfig.dashed.dashSize}
              gapSize={arrowConfig.dashed.gapSize}
            />
          );
        }
      }
    }

    // Check Y direction
    if (dyLeft !== 0 || dyRight !== 0) {
      const sampleAxisY = GLOBAL_Y.clone();
      const useLineArrow = isLoadAlignedWithBeam(sampleAxisY, localX);

      if (useLineArrow) {
        positions.forEach((p, i) => {
          const t = i / (positions.length - 1);
          const mag = dyLeft + (dyRight - dyLeft) * t;
          if (mag !== 0) {
            const axisY = GLOBAL_Y.clone().multiplyScalar(
              useLineArrow ? (mag > 0 ? 1 : -1) : mag > 0 ? -1 : 1
            );
            elements.push(
              <group key={`vdy-${i}`}>
                {drawVArrowHead(
                  p,
                  axisY,
                  col,
                  zoomScale,
                  camera,
                  localY,
                  localZ
                )}
              </group>
            );
          }
        });
      } else {
        const tailPoints: [number, number, number][] = [];
        positions.forEach((p, i) => {
          const t = i / (positions.length - 1);
          const mag = dyLeft + (dyRight - dyLeft) * t;
          const axisY = GLOBAL_Y.clone().multiplyScalar(
            useLineArrow ? (mag > 0 ? 1 : -1) : mag > 0 ? -1 : 1
          );
          const rawScale = Math.abs(mag) / normY;
          const tailScale = Math.max(0.1, Math.min(1, rawScale));
          elements.push(
            <group key={`vdy-${i}`}>
              {drawTriangularArrow(
                p,
                axisY,
                col,
                zoomScale,
                camera,
                localY,
                localZ,
                tailScale
              )}
            </group>
          );
          const tail = computeTriTailEnd(p, axisY, zoomScale, tailScale);
          tailPoints.push([tail.x, tail.y, tail.z]);
        });
        if (tailPoints.length >= 2) {
          elements.push(
            <Line
              key="vdy-dashed"
              points={tailPoints}
              color={col}
              lineWidth={1}
              dashed
              dashSize={arrowConfig.dashed.dashSize}
              gapSize={arrowConfig.dashed.gapSize}
            />
          );
        }
      }
    }

    // Check Z direction
    if (dzLeft !== 0 || dzRight !== 0) {
      const sampleAxisZ = GLOBAL_Z.clone();
      const useLineArrow = isLoadAlignedWithBeam(sampleAxisZ, localX);

      if (useLineArrow) {
        positions.forEach((p, i) => {
          const t = i / (positions.length - 1);
          const mag = dzLeft + (dzRight - dzLeft) * t;
          if (mag !== 0) {
            const axisZ = GLOBAL_Z.clone().multiplyScalar(
              useLineArrow ? (mag > 0 ? 1 : -1) : mag > 0 ? -1 : 1
            );
            elements.push(
              <group key={`vdz-${i}`}>
                {drawVArrowHead(
                  p,
                  axisZ,
                  col,
                  zoomScale,
                  camera,
                  localY,
                  localZ
                )}
              </group>
            );
          }
        });
      } else {
        const tailPoints: [number, number, number][] = [];
        positions.forEach((p, i) => {
          const t = i / (positions.length - 1);
          const mag = dzLeft + (dzRight - dzLeft) * t;
          const axisZ = GLOBAL_Z.clone().multiplyScalar(
            useLineArrow ? (mag > 0 ? 1 : -1) : mag > 0 ? -1 : 1
          );
          const rawScale = Math.abs(mag) / normZ;
          const tailScale = Math.max(0.1, Math.min(1, rawScale));
          elements.push(
            <group key={`vdz-${i}`}>
              {drawTriangularArrow(
                p,
                axisZ,
                col,
                zoomScale,
                camera,
                localY,
                localZ,
                tailScale
              )}
            </group>
          );
          const tail = computeTriTailEnd(p, axisZ, zoomScale, tailScale);
          tailPoints.push([tail.x, tail.y, tail.z]);
        });
        if (tailPoints.length >= 2) {
          elements.push(
            <Line
              key="vdz-dashed"
              points={tailPoints}
              color={col}
              lineWidth={1}
              dashed
              dashSize={arrowConfig.dashed.dashSize}
              gapSize={arrowConfig.dashed.gapSize}
            />
          );
        }
      }
    }

    return <group>{elements}</group>;
  };

  const renderConcentratedLoad = () => {
    const {
      fx = 0,
      fy = 0,
      fz = 0,
      referenceNode = "node1",
      percentageFromNode = 50,
    } = beamTruss;
    const t =
      referenceNode === "node1"
        ? percentageFromNode / 100
        : (100 - percentageFromNode) / 100;
    const p = startPoint.clone().lerp(endPoint, t);
    const elements: React.ReactNode[] = [];
    const col = "#e62e00";

    // X-axis - calculate direction AFTER useLineArrow check
    if (fx !== 0) {
      const useLineArrow = isLoadAlignedWithBeam(GLOBAL_X, localX);
      const axisX = GLOBAL_X.clone().multiplyScalar(
        useLineArrow ? (fx > 0 ? 1 : -1) : fx > 0 ? -1 : 1
      );

      if (useLineArrow) {
        elements.push(
          <group key="cfx">
            {drawVArrowHead(p, axisX, col, zoomScale, camera, localY, localZ)}
          </group>
        );
      } else {
        elements.push(
          <group key="cfx">
            {drawTriangularArrow(
              p,
              axisX,
              col,
              zoomScale,
              camera,
              localY,
              localZ
            )}
          </group>
        );
      }
    }

    // Y-axis - calculate direction AFTER useLineArrow check
    if (fy !== 0) {
      const useLineArrow = isLoadAlignedWithBeam(GLOBAL_Y, localX);
      const axisY = GLOBAL_Y.clone().multiplyScalar(
        useLineArrow ? (fy > 0 ? 1 : -1) : fy > 0 ? -1 : 1
      );

      if (useLineArrow) {
        elements.push(
          <group key="cfy">
            {drawVArrowHead(p, axisY, col, zoomScale, camera, localY, localZ)}
          </group>
        );
      } else {
        elements.push(
          <group key="cfy">
            {drawTriangularArrow(
              p,
              axisY,
              col,
              zoomScale,
              camera,
              localY,
              localZ
            )}
          </group>
        );
      }
    }

    // Z-axis - calculate direction AFTER useLineArrow check
    if (fz !== 0) {
      const useLineArrow = isLoadAlignedWithBeam(GLOBAL_Z, localX);
      const axisZ = GLOBAL_Z.clone().multiplyScalar(
        useLineArrow ? (fz > 0 ? 1 : -1) : fz > 0 ? -1 : 1
      );

      if (useLineArrow) {
        elements.push(
          <group key="cfz">
            {drawVArrowHead(p, axisZ, col, zoomScale, camera, localY, localZ)}
          </group>
        );
      } else {
        elements.push(
          <group key="cfz">
            {drawTriangularArrow(
              p,
              axisZ,
              col,
              zoomScale,
              camera,
              localY,
              localZ
            )}
          </group>
        );
      }
    }

    return <group>{elements}</group>;
  };

  switch (beamTruss.type) {
    case "distributed_uniform":
      return renderDistributedUniform();
    case "distributed_variable":
      return renderDistributedVariable();
    case "concentrated_load":
      return renderConcentratedLoad();
    default:
      return null;
  }
};
